import React, { useState } from 'react';
import { Modal, Button, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { CME_KHOA_DAO_TAO_API } from '../../../../utils/apiUrl';
import { Post, PostFormData } from '../../../../utils/httpProvider';
import { messageToast } from '../../../../utils/define';
import UserInfoCell from '../duyetHoSo/UserInfoCell';

const CMEEventImportModal = ({ isOpen, handleClose, eventData }) => {
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [step, setStep] = useState(1); // 1: Chọn file, 2: Preview

    const previewMutation = useMutation((formData) =>
        PostFormData(`${CME_KHOA_DAO_TAO_API.PREVIEW_IMPORT}/${eventData.id}`, formData)
    );

    const confirmMutation = useMutation((members) =>
        Post(`${CME_KHOA_DAO_TAO_API.CONFIRM_IMPORT}/${eventData.id}`, null, members)
    );

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = () => {
        if (!file) {
            toast.error("Vui lòng chọn file Excel");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        previewMutation.mutate(formData, {
            onSuccess: (res) => {
                const data = res?.$values || res || [];
                setPreviewData(data);
                setStep(2);
                setIsUploading(false);
            },
            onError: (err) => {
                toast.error(err?.message || "Lỗi khi đọc file Excel");
                setIsUploading(false);
            }
        });
    };

    const handleConfirm = () => {
        const validMembers = previewData.filter(m => m.isValid);
        if (validMembers.length === 0) {
            toast.error("Không có dữ liệu hợp lệ để import");
            return;
        }

        confirmMutation.mutate(validMembers, {
            onSuccess: (res) => {
                if (res === true || res.status === 200) {
                    toast.success("Import điểm thành công!");
                    handleClose();
                    // Reset state
                    setStep(1);
                    setFile(null);
                    setPreviewData([]);
                }
            },
            onError: (err) => {
                toast.error(err?.message || "Lỗi khi lưu dữ liệu");
            }
        });
    };

    const resetModal = () => {
        setStep(1);
        setFile(null);
        setPreviewData([]);
        handleClose();
    };

    const validCount = previewData.filter(m => m.isValid).length;
    const errorCount = previewData.length - validCount;

    return (
        <Modal show={isOpen} onHide={resetModal} size="xl" backdrop="static" style={{ zIndex: '9999' }}>
            <Modal.Header closeButton className="bg-success text-white">
                <Modal.Title>
                    <i className="fas fa-file-import mr-2"></i>Import danh sách cấp điểm: {eventData?.tenKhoaDaoTao}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {step === 1 ? (
                    <div className="text-center py-5">
                        <i className="fas fa-file-excel fa-4x text-success mb-3"></i>
                        <h5>Tải lên file danh sách thành viên tham gia</h5>
                        <p className="text-muted">Định dạng hỗ trợ: .xlsx, .xls. Cấu trúc: <b>STT, CCCD/Mã NV, Vai trò.</b></p>
                        <div className="d-flex justify-content-center mt-4">
                            <div className="custom-file w-50">
                                <input type="file" className="custom-file-input" id="customFile" onChange={handleFileChange} accept=".xlsx, .xls" />
                                <label className="custom-file-label text-left" htmlFor="customFile">
                                    {file ? file.name : "Chọn file Excel..."}
                                </label>
                            </div>
                        </div>
                        <Button variant="success" className="mt-4 px-5" onClick={handleUpload} disabled={!file || isUploading}>
                            {isUploading ? <><Spinner animation="border" size="sm" className="mr-2" /> Đang xử lý...</> : "Tải lên và xem trước"}
                        </Button>
                    </div>
                ) : (
                    <>
                        <Alert variant={errorCount > 0 ? "warning" : "info"} className="d-flex justify-content-between align-items-center font-weight-bold">
                            <div>
                                <i className="fas fa-info-circle mr-2"></i>
                                Tổng số: {previewData.length} dòng |
                                <span className="text-success ml-2">Hợp lệ: {validCount}</span> |
                                <span className="text-danger ml-2">Lỗi: {errorCount}</span>
                            </div>
                            <Button variant="outline-secondary" size="sm" onClick={() => setStep(1)}>Chọn lại file</Button>
                        </Alert>

                        <div className="table-responsive" style={{ maxHeight: '500px' }}>
                            <Table striped bordered hover size="sm">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th>Mã NV / CCCD</th>
                                        <th>Họ tên</th>
                                        <th>Vai trò</th>
                                        <th className="text-center">Số giờ CME</th>
                                        <th>Trạng thái / Ghi chú</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((item, idx) => (
                                        <tr key={idx} className={item.isValid ? "" : "table-danger"}>
                                            <td>
                                                <UserInfoCell userId={item.userId} mode="username" />
                                            </td>
                                            <td>
                                                <UserInfoCell userId={item.userId} mode="name" />
                                            </td>
                                            <td>{item.vaiTro}</td>
                                            <td className="text-center font-weight-bold">{item.points}</td>
                                            <td>
                                                {item.isValid ?
                                                    <Badge variant="success"><i className="fas fa-check mr-1"></i>Hợp lệ</Badge> :
                                                    <span className="text-danger small font-weight-bold">{item.errorMessage}</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={resetModal}>Đóng</Button>
                {step === 2 && (
                    <Button variant="primary" onClick={handleConfirm} disabled={validCount === 0 || confirmMutation.isLoading}>
                        {confirmMutation.isLoading ? 'Đang lưu dữ liệu...' : `Xác nhận Import ${validCount} bản ghi`}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CMEEventImportModal;
